/**
 * Document folders — classification with full CRUD. Uploads land in the folder
 * you have selected; deleting a folder never deletes paperwork (documents fall
 * back to Unfiled).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  FolderClosed, FolderOpen, FilePlus2, Pencil, Trash2, UploadCloud, FileText, Eye, Download, Loader2, Check, X, FolderInput,
} from 'lucide-react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { uploadContractDocuments } from '@/utils/contractUpload';
import { openContractDoc, downloadContractDoc } from '@/utils/contractDocUrl';
import { fmtDate, apiError } from './portfolioUi';

const kb = (n) => (n == null ? '' : n > 1e9 ? `${(n / 1e9).toFixed(2)} GB` : n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const DocumentFolders = ({ contractId, onChanged }) => {
  const [tree, setTree] = useState(null);
  const [active, setActive] = useState('unfiled');
  const [renaming, setRenaming] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef(null);

  const toggleSelected = (path) => setSelected(prev => {
    const next = new Set(prev);
    next.has(path) ? next.delete(path) : next.add(path);
    return next;
  });

  const toggleSelectAll = () => setSelected(prev => {
    const paths = (current.documents || []).map(d => d.storage_path || d.file_url);
    return paths.every(p => prev.has(p)) ? new Set() : new Set(paths);
  });

  const removeDocs = async (paths, label) => {
    if (!paths.length) return;
    const what = label || `${paths.length} document(s)`;
    if (!window.confirm(`Delete ${what}? This removes the paperwork from this contract.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post(`/contracts/contracts/${contractId}/documents/delete`,
        { storage_paths: paths });
      toast.success(`${data.deleted} document(s) deleted`);
      setSelected(new Set());
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e, 'Delete failed'));
    } finally { setDeleting(false); }
  };

  const download = async (d) => {
    const path = d.storage_path || d.file_url;
    setDownloading(path);
    try {
      await downloadContractDoc(contractId, path, d.file_name);
    } catch (e) {
      toast.error(apiError(e, 'Download failed'));
    } finally { setDownloading(null); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/contracts/contracts/${contractId}/folders`);
      setTree(data);
    } catch { toast.error('Could not load document folders'); }
  }, [contractId]);

  useEffect(() => { load(); }, [load]);

  const folders = tree?.folders || [];
  const current = active === 'unfiled'
    ? { folder_id: 'unfiled', name: 'Unfiled', documents: tree?.unfiled || [] }
    : folders.find(f => f.folder_id === active) || { name: '—', documents: [] };

  const docPathsNow = (current.documents || []).map(d => d.storage_path || d.file_url);
  const everySelected = docPathsNow.length > 0 && docPathsNow.every(p => selected.has(p));

  const createFolder = async () => {
    if (!draftName.trim()) { setCreating(false); return; }
    try {
      const { data } = await api.post(`/contracts/contracts/${contractId}/folders`, { name: draftName.trim() });
      setDraftName(''); setCreating(false);
      await load();
      setActive(data.folder_id);
    } catch (e) { toast.error(apiError(e, 'Could not create the folder')); }
  };

  const rename = async (folderId) => {
    if (!draftName.trim()) { setRenaming(null); return; }
    try {
      await api.patch(`/contracts/contracts/${contractId}/folders/${folderId}`, { name: draftName.trim() });
      setRenaming(null); setDraftName('');
      await load();
    } catch (e) { toast.error(apiError(e, 'Rename failed')); }
  };

  const remove = async (folderId) => {
    try {
      await api.delete(`/contracts/contracts/${contractId}/folders/${folderId}`);
      if (active === folderId) setActive('unfiled');
      await load();
      toast.success('Folder deleted — its documents moved to Unfiled');
    } catch { toast.error('Delete failed'); }
  };

  const moveDoc = async (storage_path, folder_id) => {
    try {
      await api.post(`/contracts/contracts/${contractId}/documents/move`,
        { storage_path, folder_id: folder_id === 'unfiled' ? null : folder_id });
      await load();
    } catch { toast.error('Could not move the document'); }
  };

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const saved = await uploadContractDocuments(contractId, files, {
        folderId: active,
        onProgress: setProgress,
      });
      toast.success(`${saved.length || files.length} document(s) filed under ${current.name}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e, 'Upload failed'));
    } finally {
      setBusy(false);
      setProgress(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  if (!tree) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--brand-accent)' }} /></div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6" data-testid="document-folders">
      {/* folder rail */}
      <aside className="surface-card rounded-xl p-3 h-fit">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] px-2 pb-2" style={{ color: 'var(--text-muted)' }}>
          Folders · {tree.total_documents} documents
        </p>
        {[{ folder_id: 'unfiled', name: 'Unfiled', documents: tree.unfiled || [], seeded: true }, ...folders].map(f => (
          <div key={f.folder_id} className="group flex items-center gap-1.5 rounded-lg px-2 py-1.5"
               style={{ background: active === f.folder_id ? 'var(--active-bg)' : 'transparent' }}>
            {renaming === f.folder_id ? (
              <>
                <Input value={draftName} onChange={(e) => setDraftName(e.target.value)}
                       onKeyDown={(e) => e.key === 'Enter' && rename(f.folder_id)}
                       className="h-7 text-xs" autoFocus data-testid="folder-rename-input" />
                <button onClick={() => rename(f.folder_id)} data-testid="folder-rename-save"><Check className="w-3.5 h-3.5" style={{ color: '#15803D' }} /></button>
                <button onClick={() => setRenaming(null)}><X className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} /></button>
              </>
            ) : (
              <>
                <button onClick={() => setActive(f.folder_id)} className="flex items-center gap-2 flex-1 min-w-0 text-left"
                        data-testid={`folder-${f.name.replace(/\s+/g, '-').toLowerCase()}`}>
                  {active === f.folder_id ? <FolderOpen className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--brand-accent)' }} />
                                          : <FolderClosed className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--text-muted)' }} />}
                  <span className="text-xs truncate" style={{ color: 'var(--text-primary)' }}>{f.name}</span>
                  <span className="ml-auto text-[10px] tabular-nums" style={{ color: 'var(--text-muted)' }}>{f.documents?.length || 0}</span>
                </button>
                {f.folder_id !== 'unfiled' && (
                  <span className="hidden group-hover:flex items-center gap-1">
                    <button onClick={() => { setRenaming(f.folder_id); setDraftName(f.name); }} data-testid={`folder-rename-${f.folder_id}`}>
                      <Pencil className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                    </button>
                    <button onClick={() => remove(f.folder_id)} data-testid={`folder-delete-${f.folder_id}`}>
                      <Trash2 className="w-3 h-3" style={{ color: '#B91C1C' }} />
                    </button>
                  </span>
                )}
              </>
            )}
          </div>
        ))}
        {creating ? (
          <div className="flex items-center gap-1.5 px-2 py-1.5">
            <Input value={draftName} onChange={(e) => setDraftName(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && createFolder()}
                   placeholder="Folder name" className="h-7 text-xs" autoFocus data-testid="folder-new-input" />
            <button onClick={createFolder} data-testid="folder-new-save"><Check className="w-3.5 h-3.5" style={{ color: '#15803D' }} /></button>
          </div>
        ) : (
          <button onClick={() => { setCreating(true); setDraftName(''); }}
                  className="mt-2 w-full inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs"
                  style={{ color: 'var(--brand-accent)' }} data-testid="folder-new">
            <FilePlus2 className="w-3.5 h-3.5" /> New folder
          </button>
        )}
      </aside>

      {/* folder contents */}
      <section className="surface-card rounded-xl p-5" data-testid="folder-contents">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{current.name}</h4>
          <div className="flex items-center gap-2">
            {!!docPathsNow.length && (
              <>
                <button onClick={toggleSelectAll}
                        className="text-[11px] underline-offset-2 hover:underline"
                        style={{ color: 'var(--text-muted)' }}
                        data-testid="docs-select-all">
                  {everySelected ? 'Clear selection' : `Select all ${docPathsNow.length}`}
                </button>
                <button onClick={() => removeDocs([...selected])}
                        disabled={!selected.size || deleting}
                        className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs disabled:opacity-40"
                        style={{ borderColor: 'var(--border-color)', color: '#DC2626' }}
                        data-testid="docs-delete-selected">
                  {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  Delete{selected.size ? ` (${selected.size})` : ''}
                </button>
              </>
            )}
            <button onClick={() => inputRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs"
                    style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    data-testid="folder-upload-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />} Upload here
            </button>
          </div>
          <input ref={inputRef} type="file" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="document-file-input" />
        </div>

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
          className="grain rounded-xl border-2 border-dashed py-6 text-center text-xs mb-4"
          style={{ borderColor: 'var(--border-color)', background: 'var(--surface-bg)', color: 'var(--text-muted)' }}
          data-testid="document-dropzone"
        >
          Drop files to file them under “{current.name}” — any format, any size
        </div>

        {progress && (
          <div className="mb-4" data-testid="upload-progress">
            <div className="flex items-center justify-between text-[11px] mb-1" style={{ color: 'var(--text-muted)' }}>
              <span className="truncate">Uploading {progress.name} ({progress.i} of {progress.of})</span>
              <span className="tabular-nums">{progress.pct}%</span>
            </div>
            <div className="h-1 rounded-full overflow-hidden" style={{ background: 'var(--border-subtle)' }}>
              <div className="h-full transition-[width] duration-200"
                   style={{ width: `${progress.pct}%`, background: 'var(--brand-accent)' }} />
            </div>
          </div>
        )}

        <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
          {(current.documents || []).map((d, i) => {
            const path = d.storage_path || d.file_url;
            return (
            <div key={path || i} className="flex items-center gap-3 py-3" data-testid={`contract-doc-${i}`}>
              <input type="checkbox" className="shrink-0 accent-[var(--brand-accent)]"
                     checked={selected.has(path)}
                     onChange={() => toggleSelected(path)}
                     aria-label={`Select ${d.file_name}`}
                     data-testid={`contract-doc-select-${i}`} />
              <FileText className="w-4 h-4 shrink-0" style={{ color: 'var(--brand-accent)' }} />
              <div className="min-w-0 flex-1">
                <p className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>{d.file_name}</p>
                <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  {kb(d.file_size)} · {fmtDate(d.uploaded_at)}{d.risk_count ? ` · ${d.risk_count} risk(s)` : ''}
                </p>
              </div>
              <select
                value={d.folder_id || 'unfiled'}
                onChange={(e) => moveDoc(d.storage_path, e.target.value)}
                className="text-[11px] rounded-full border px-2 py-1"
                style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-secondary)' }}
                data-testid={`doc-move-${i}`}
              >
                <option value="unfiled">Unfiled</option>
                {folders.map(f => <option key={f.folder_id} value={f.folder_id}>{f.name}</option>)}
              </select>
              <button onClick={() => openContractDoc(contractId, d.storage_path || d.file_url)}
                      className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px]"
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                      data-testid={`contract-doc-open-${i}`}>
                <Eye className="w-3 h-3" /> Open
              </button>
              <button onClick={() => download(d)}
                      disabled={downloading === (d.storage_path || d.file_url)}
                      className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] disabled:opacity-60"
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                      title={`Download ${d.file_name}`}
                      data-testid={`contract-doc-download-${i}`}>
                {downloading === (d.storage_path || d.file_url)
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Download className="w-3 h-3" />} Download
              </button>
              <button onClick={() => removeDocs([path], d.file_name)}
                      className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px]"
                      style={{ borderColor: 'var(--border-color)', color: '#DC2626' }}
                      title={`Delete ${d.file_name}`}
                      data-testid={`contract-doc-delete-${i}`}>
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
            );
          })}
          {!(current.documents || []).length && (
            <p className="text-sm py-6 text-center flex items-center justify-center gap-2" style={{ color: 'var(--text-muted)' }} data-testid="folder-empty">
              <FolderInput className="w-4 h-4" /> Nothing filed in this folder yet.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default DocumentFolders;
